import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, ArrowLeft } from "lucide-react";
import aluminumDoorsImg from "@/assets/aluminum-doors.jpg";
import aluminumWindowsImg from "@/assets/aluminum-windows.jpg";
import aluminumAwningsImg from "@/assets/aluminum-awnings.jpg";
import aluminumKitchenImg from "@/assets/aluminum-kitchen.jpg";
import aluminumGlassFacadeImg from "@/assets/aluminum-glass-facade.jpg";

const AluminumProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, isRTL } = useLanguage();

  const content = {
    ar: {
      backToProducts: "العودة لمنتجات الألمنيوم",
      contactUs: "تواصل معنا للطلب",
      features: "المميزات",
      ourProjects: "مشاريعنا وأعمالنا",
      products: {
        "doors": {
          title: "أبواب ألمنيوم",
          description: "تصميمات أبواب فخمة واعتيادية بمواصفات عالية الجودة",
          longDescription: "نقدم تشكيلة واسعة من أبواب الألمنيوم الفاخرة والاعتيادية بمواصفات عالية الجودة تجمع بين الأناقة والمتانة. حلول متكاملة للمنازل والفلل والشركات والمباني التجارية مع ضمان شامل وخدمة تركيب احترافية.",
          features: ["عزل حراري ومائي ممتاز", "مقاومة للصدأ والتآكل", "تصاميم متنوعة", "سهولة الصيانة", "ضمان شامل"],
          images: [aluminumDoorsImg, aluminumWindowsImg, aluminumGlassFacadeImg]
        },
        "windows": {
          title: "نوافذ ألمنيوم",
          description: "شبابيك ألمنيوم حديثة بجودة عالية",
          longDescription: "نوافذ ألمنيوم حديثة بجودة عالية تلبي الاحتياجات وتتماشى مع المناخ. عزل حراري ومائي ممتاز ومقاومة للغبار والضوضاء مع تصاميم عصرية تناسب جميع أنماط المباني.",
          features: ["عزل صوتي متميز", "عزل حراري عالي", "مقاومة للغبار", "زجاج مزدوج", "تصاميم عصرية"],
          images: [aluminumWindowsImg, aluminumDoorsImg, aluminumGlassFacadeImg]
        },
        "shades": {
          title: "مظلات وسواتر",
          description: "كافة أنواع المظلات والسواتر وكاسرات الشمس",
          longDescription: "نقدم كافة أنواع المظلات والسواتر وكاسرات الشمس والرياح بتصاميم عصرية. حماية من أشعة الشمس والأمطار والرياح مع الحفاظ على الجمال المعماري وتوفير الخصوصية.",
          features: ["حماية من الشمس", "مقاومة للرياح", "تصاميم متنوعة", "ألوان متعددة", "متانة عالية"],
          images: [aluminumAwningsImg, aluminumGlassFacadeImg, aluminumDoorsImg]
        },
        "kitchens": {
          title: "مطابخ ألمنيوم",
          description: "مطابخ عصرية وحديثة من الألمنيوم",
          longDescription: "مطابخ عصرية وحديثة مصنوعة من الألمنيوم عالي الجودة بتصميمات مميزة. تجمع بين الجمال والمتانة وسهولة التنظيف ومقاومة الحرارة والرطوبة لتدوم لسنوات طويلة.",
          features: ["مقاومة للرطوبة", "سهولة التنظيف", "مقاومة للحرارة", "تصاميم عصرية", "عمر افتراضي طويل"],
          images: [aluminumKitchenImg, aluminumDoorsImg, aluminumWindowsImg]
        },
        "facades": {
          title: "واجهات زجاجية",
          description: "واجهات زجاجية للمباني والأبراج",
          longDescription: "واجهات زجاجية للمباني والأبراج بتصاميم مبتكرة وجودة عالية. تضيف لمسة عصرية وأنيقة للمباني مع توفير إضاءة طبيعية وإطلالات رائعة وعزل حراري متميز.",
          features: ["إضاءة طبيعية", "عزل حراري", "مظهر عصري", "زجاج عاكس", "صيانة سهلة"],
          images: [aluminumGlassFacadeImg, aluminumWindowsImg, aluminumDoorsImg]
        }
      }
    },
    en: {
      backToProducts: "Back to Aluminum Products",
      contactUs: "Contact Us to Order",
      features: "Features",
      ourProjects: "Our Projects & Work",
      products: {
        "doors": {
          title: "Aluminum Doors",
          description: "Luxurious and standard door designs with high-quality specifications",
          longDescription: "We offer a wide range of luxurious and standard aluminum doors with high-quality specifications combining elegance and durability. Complete solutions for homes, villas, companies, and commercial buildings with comprehensive warranty and professional installation service.",
          features: ["Excellent thermal and water insulation", "Rust and corrosion resistant", "Various designs", "Easy maintenance", "Comprehensive warranty"],
          images: [aluminumDoorsImg, aluminumWindowsImg, aluminumGlassFacadeImg]
        },
        "windows": {
          title: "Aluminum Windows",
          description: "Modern high-quality aluminum windows",
          longDescription: "Modern high-quality aluminum windows that meet needs and adapt to the climate. Excellent thermal and water insulation with dust and noise resistance and modern designs suitable for all building styles.",
          features: ["Superior sound insulation", "High thermal insulation", "Dust resistant", "Double glazing", "Modern designs"],
          images: [aluminumWindowsImg, aluminumDoorsImg, aluminumGlassFacadeImg]
        },
        "shades": {
          title: "Shades & Screens",
          description: "All types of shades, screens, and sun breakers",
          longDescription: "We offer all types of shades, screens, and sun/wind breakers with modern designs. Protection from sunlight, rain, and wind while maintaining architectural beauty and providing privacy.",
          features: ["Sun protection", "Wind resistant", "Various designs", "Multiple colors", "High durability"],
          images: [aluminumAwningsImg, aluminumGlassFacadeImg, aluminumDoorsImg]
        },
        "kitchens": {
          title: "Aluminum Kitchens",
          description: "Modern kitchens made from high-quality aluminum",
          longDescription: "Modern kitchens made from high-quality aluminum with distinctive designs. Combining beauty, durability, easy cleaning, and heat and moisture resistance to last for years.",
          features: ["Moisture resistant", "Easy to clean", "Heat resistant", "Modern designs", "Long lifespan"],
          images: [aluminumKitchenImg, aluminumDoorsImg, aluminumWindowsImg]
        },
        "facades": {
          title: "Glass Facades",
          description: "Glass facades for buildings and towers",
          longDescription: "Glass facades for buildings and towers with innovative designs and high quality. Adding a modern and elegant touch to buildings with natural lighting, great views, and excellent thermal insulation.",
          features: ["Natural lighting", "Thermal insulation", "Modern appearance", "Reflective glass", "Easy maintenance"],
          images: [aluminumGlassFacadeImg, aluminumWindowsImg, aluminumDoorsImg]
        }
      }
    }
  };

  const t = content[language];
  const product = slug ? t.products[slug as keyof typeof t.products] : null;

  if (!product) {
    return (
      <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">{language === "ar" ? "المنتج غير موجود" : "Product Not Found"}</h1>
          <Link to="/aluminum">
            <Button className="bg-primary hover:bg-primary/90">{t.backToProducts}</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container mx-auto px-4 py-12">
        <Link to="/aluminum" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-8 font-kufi">
          <ArrowIcon className="w-4 h-4" />
          {t.backToProducts}
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-12">
          <h1 className="text-4xl font-kufi font-bold text-primary mb-4">{product.title}</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{product.description}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="bg-card rounded-xl shadow-lg p-8 mb-12">
          <p className="text-lg leading-relaxed text-card-foreground">{product.longDescription}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="bg-primary/5 rounded-xl p-8 mb-12">
          <h2 className="text-2xl font-kufi font-bold text-primary mb-6">{t.features}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {product.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 bg-card p-4 rounded-lg shadow-sm">
                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                <span className="text-card-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="mb-12">
          <h2 className="text-2xl font-kufi font-bold text-primary mb-8 text-center">{t.ourProjects}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.images.map((image, index) => (
              <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: index * 0.1 }} className="group relative overflow-hidden rounded-xl shadow-lg aspect-[4/3]">
                <img src={image} alt={`${product.title} ${index + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} className="text-center bg-primary/10 rounded-xl p-8">
          <Link to="/contact">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-kufi text-lg px-8">{t.contactUs}</Button>
          </Link>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default AluminumProductDetail;
